import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Coins,
  Eye,
  FileQuestion,
  FolderTree,
  GraduationCap,
  Layers,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import { api } from '../../api';
import { useRBAC } from '../../contexts/RBACContext';
import GComprisRewardModal from '../../components/admin/GComprisRewardModal';
import LoadingState from '../../components/common/LoadingState';
import { confirmDialog, escapeHtml, resultDialog } from '../../utils/swalDialogs';
import AssignCourseModal from './AssignCourseModal';
import ContentDetailsDialog from './ContentDetailsDialog';
import { LIGHT_COURSE_LIST } from './courseThumbnails';
import { ITEM_TYPES } from './courseViewShared';
import {
  CategoryBadge,
  CategorySidebar,
  SubcategoryActions,
  SubcategoryCrumbs,
  addContentPath,
  canAddSubcategoryUnder,
  canDeleteRow,
  categoryIcon,
  categoryLabel,
  categoryMeta,
  contentCoins,
  contentRowsOf,
  countSubcategories,
  courseInSubcategory,
  courseSubcategoryChain,
  isGComprisRow,
  isLastRow,
  promptAddCategory,
  promptAddSidebarSubcategory,
  subcategoryChain,
  useCourseCategories,
  useCourseSubcategories,
} from './coursesShared';

const PAGE_SIZE = 10;
const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

/**
 * Courses Management (admin) — categories sidebar, totals, and the content table (every song, scene
 * or story added with Add New Content, not the courses they are saved in) with view / edit / delete;
 * a subcategory picked in the sidebar narrows it. SECURITY: requires 'LMS Management' 'Manage'.
 */
export default function AdminCourseDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, isLoading: rbacLoading, permissions } = useRBAC();
  const { categories, reload: reloadCategories } = useCourseCategories();
  const { nodesFor, reload: reloadSubcategories } = useCourseSubcategories();

  const [courses, setCourses] = useState([]);
  const [summary, setSummary] = useState({ totals: {}, courses: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ?category=&sub= (a subcategory picked in the sidebar of a course's page) opens filtered.
  const [category, setCategory] = useState(() => new URLSearchParams(location.search).get('category') || '');
  const [subcategory, setSubcategory] = useState(() => new URLSearchParams(location.search).get('sub') || '');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [details, setDetails] = useState(null); // the content whose details are open
  const [assignFor, setAssignFor] = useState(undefined); // undefined = closed, null = no preselection
  const [gcomprisOpen, setGcomprisOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  // SECURITY CHECK: redirect unauthorized users
  useEffect(() => {
    if (rbacLoading || !Object.keys(permissions).length) return;
    if (!hasPermission('LMS Management', 'Manage')) navigate('/access-denied');
  }, [hasPermission, navigate, rbacLoading, permissions]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // The totals show on their own, even if the list fails.
      api.get('/api/v2/lms/admin/courses/summary')
        .then((summaryRes) => { if (summaryRes?.data?.data) setSummary(summaryRes.data.data); })
        .catch(() => {});
      // Without thumbnails (each shown row loads its own, useCourseThumbnails): with them the list
      // was ~17 MB and took longer than the app waits, so it failed.
      const coursesRes = await api.get('/api/v2/lms/admin/courses', { params: LIGHT_COURSE_LIST });
      setCourses(coursesRes.data?.data || []);
    } catch (err) {
      setError('Failed to load the content. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!rbacLoading && hasPermission('LMS Management', 'Manage')) load();
  }, [rbacLoading, hasPermission, load]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const close = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [moreOpen]);

  const categoryNodes = useMemo(() => nodesFor(category), [nodesFor, category]);
  // The picked subcategory and the levels above it (English > Action Songs > Vocal Songs).
  const chain = useMemo(() => subcategoryChain(categoryNodes, subcategory), [categoryNodes, subcategory]);
  const subcategoryNode = chain.length ? chain[chain.length - 1] : null;
  const selectCategory = (name) => {
    setCategory(name);
    setSubcategory('');
  };
  // Picking the selected subcategory again goes back to the whole category. A last subcategory
  // (nothing under it) opens Add New Content for it instead.
  const selectSubcategory = (name, node) => {
    if (isLastRow(node)) {
      openLastRow(name, node);
      return;
    }
    setSubcategory(name === category && node.key === subcategory ? '' : node.key);
    setCategory(name);
  };
  // Add New Content's Back (and the browser's) comes back here with that subcategory's card open.
  const openLastRow = (name, node) => {
    const back = `/admin/courses?${new URLSearchParams({ category: name, sub: node.key })}`;
    navigate(back, { replace: true });
    navigate(addContentPath(name, node.key), { state: { from: back } });
  };

  // The page lists content (Action Song 1, Scene 2 ...: every chapter), not the courses it is saved
  // in: all of it, or the picked category's / subcategory's (its rows under it included).
  const contentRows = useMemo(() => contentRowsOf(courses), [courses]);
  // Where a content sits under its category: Action Songs › Vocal Songs.
  const levelsOf = useCallback((course) => courseSubcategoryChain(nodesFor(course.category), course), [nodesFor]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contentRows.filter(({ course, chapter }) => {
      if (category && course.category !== category) return false;
      if (subcategoryNode && !courseInSubcategory(course, subcategoryNode)) return false;
      if (status !== 'all' && course.status !== status) return false;
      const words = [chapter.title, chapter.description, course.category, categoryLabel(course.category), ...levelsOf(course).map((node) => node.label)];
      if (q && !words.some((v) => (v || '').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [contentRows, category, subcategoryNode, status, search, levelsOf]);
  useEffect(() => setPage(1), [category, subcategory, status, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage]
  );

  const totals = summary.totals || {};
  const totalStudents = totals.totalStudents || 0;
  const liveContent = contentRows.filter(({ course }) => course.status !== 'archived').length;

  const deleteContent = async ({ course, module, chapter }) => {
    const ok = await confirmDialog({
      title: 'Delete this content?',
      html: `<b>${escapeHtml(chapter.title || 'Untitled')}</b> and its PDF, audio, video, game and quiz will be removed for students too. This can't be undone.`,
      confirmText: 'Yes, delete',
      cancelText: 'Keep it',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/api/v2/lms/admin/courses/${course._id}/modules/${module._id}/chapters/${chapter._id}`);
      resultDialog('success', 'Content deleted');
      load();
    } catch (err) {
      resultDialog('error', 'Could not delete the content', err?.response?.data?.error || err?.response?.data?.message || 'Please try again.');
    }
  };
  // Content is edited on its course's page, in edit mode.
  const editContent = ({ course }) => navigate(`/admin/courses/${course._id}/view?edit=1`);

  const addCategory = async () => {
    const created = await promptAddCategory(categories);
    if (created) reloadCategories();
  };

  const goAddContent = (categoryName, courseId) => {
    const params = new URLSearchParams({ category: categoryName });
    if (courseId) params.set('course', courseId);
    else if (subcategory && categoryName === category) params.set('sub', subcategory);
    // The form's Back button comes back here with the same category / subcategory open.
    const here = new URLSearchParams();
    if (category) here.set('category', category);
    if (category && subcategory) here.set('sub', subcategory);
    navigate(`/admin/courses/content/new?${params.toString()}`, { state: { from: `/admin/courses${here.toString() ? `?${here}` : ''}` } });
  };

  // The content in the picked category (not archived), for each subcategory's count.
  const categoryContent = useMemo(
    () => contentRows.filter(({ course }) => course.category === category && course.status !== 'archived'),
    [contentRows, category]
  );
  // "+ Add Subcategory" adds one under the picked subcategory (or the category itself).
  const addSidebarSubcategory = async (name, node) => {
    if (await promptAddSidebarSubcategory(name, nodesFor(name), node?.key)) reloadSubcategories();
  };
  const where = [categoryLabel(category), ...chain.map((node) => node.label)].filter(Boolean).join(' › ');
  const here = subcategoryNode?.label || categoryLabel(category);
  const childNodes = subcategoryNode ? subcategoryNode.children : categoryNodes;
  const canAddHere = canAddSubcategoryUnder(categoryNodes, subcategoryNode?.key);
  // Computer Apps › GCompris (the built-in row) has the GCompris coin settings.
  const isGCompris = isGComprisRow(category, subcategoryNode);
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  // "Delete Subcategory" / "Delete Category" for the picked row, built in or added (a built-in one is
  // hidden). The content in it (any status, archived too) has to be deleted first, so none is left
  // without a place; the empty courses that held it (never shown) go with the row.
  const hereCategoryId = categoryMeta(categories, category)._id;
  const canDeleteHere = canDeleteRow(category, subcategoryNode) && (Boolean(subcategoryNode) || Boolean(hereCategoryId));
  const hereKind = subcategoryNode ? 'subcategory' : 'category';
  const deleteHere = async () => {
    const node = subcategoryNode;
    const inside = courses.filter((c) => c.category === category && (!node || courseInSubcategory(c, node)));
    const contentInside = contentRowsOf(inside).length;
    if (contentInside) {
      resultDialog('warning', `Can't delete this ${hereKind} yet`, `"${here}" still has ${plural(contentInside, 'content', 'contents')}. Delete ${contentInside === 1 ? 'it' : 'them'} first.`);
      return;
    }
    const below = countSubcategories(childNodes);
    // Built-in rows and every category come back when their name is added again in the same place.
    const comesBack = !node
      ? ' Adding a category with the same name brings it back.'
      : node.kind !== 'added'
        ? ' Adding a subcategory with the same name in the same place brings it back.'
        : '';
    const ok = await confirmDialog({
      title: `Delete this ${hereKind}?`,
      html: `<b>${escapeHtml(here)}</b> will be removed from the Courses sidebar and the students' Learning Zone.${below ? ` The ${plural(below, 'subcategory', 'subcategories')} inside it will be deleted too.` : ''}${comesBack}`,
      confirmText: 'Yes, delete',
      cancelText: `Keep ${hereKind}`,
      danger: true,
    });
    if (!ok) return;
    try {
      for (const course of inside) await api.delete(`/api/v2/lms/admin/courses/${course._id}`);
      if (inside.length) load();
      if (node?.kind === 'added') await api.delete(`/api/v2/lms/admin/courses/subcategories/${node.key}`);
      else if (node) await api.delete(`/api/v2/lms/admin/courses/subcategories/${node.key}`, { params: { category } });
      else await api.delete(`/api/v2/lms/admin/courses/categories/${hereCategoryId}`);
      resultDialog('success', node ? 'Subcategory deleted' : 'Category deleted');
      reloadSubcategories();
      // Back to the row it was in.
      if (node) setSubcategory(chain.length > 1 ? chain[chain.length - 2].key : '');
      else {
        selectCategory('');
        reloadCategories();
      }
    } catch (err) {
      resultDialog('error', `Could not delete the ${hereKind}`, err?.response?.data?.error || 'Please try again.');
    }
  };

  if (rbacLoading) return <LoadingState message="Loading permissions..." fullScreen />;

  return (
    <div className="cm-page">
      <div className="cm-layout">
        <CategorySidebar
          categories={categories}
          selected={category}
          onSelect={selectCategory}
          onAddCategory={addCategory}
          subcategoryNodes={nodesFor}
          selectedSubcategory={subcategory}
          onSelectSubcategory={selectSubcategory}
        />

        <main className="cm-main">
          <header className="cm-header">
            <div className="cm-title">
              <span className="cm-title__icon" aria-hidden="true"><GraduationCap size={28} /></span>
              <div>
                <h1>Courses Management</h1>
                <p>Create, manage and assign courses to students</p>
              </div>
            </div>
            <label className="cm-search cm-search--wide">
              <Search size={16} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search content or category..." aria-label="Search content" />
            </label>
            <div className="cm-actions">
              <button type="button" className="cm-btn cm-btn--blue" onClick={() => setAssignFor(null)}><Users size={18} />Assign Course</button>
              <button type="button" className="cm-btn cm-btn--soft" onClick={() => navigate(`/admin/courses/progress-report${category ? `?category=${encodeURIComponent(category)}${subcategory ? `&sub=${encodeURIComponent(subcategory)}` : ''}` : ''}`)}><BarChart3 size={18} />Progress Report</button>
              <div className="cm-more" ref={moreRef}>
                <button type="button" className="cm-btn cm-btn--icon" aria-label="More course tools" onClick={() => setMoreOpen((o) => !o)}><MoreHorizontal size={18} /></button>
                {moreOpen && (
                  <div className="cm-menu" role="menu">
                    <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); navigate('/admin/courses/assignments'); }}><ClipboardList size={16} />Course Assignments</button>
                    <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); navigate('/admin/quizzes'); }}><FileQuestion size={16} />Quiz Management</button>
                    {category && <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); goAddContent(category); }}><Plus size={16} />Add content to {categoryLabel(category)}</button>}
                  </div>
                )}
              </div>
            </div>
          </header>

          <section className="cm-stats">
            <div className="cm-stat cm-stat--blue">
              <span className="cm-stat__icon"><Layers size={26} /></span>
              <div><b>{liveContent}</b><span>Total Content</span><small>Across all categories</small></div>
            </div>
            <div className="cm-stat cm-stat--green">
              <span className="cm-stat__icon"><Users size={26} /></span>
              <div><b>{totalStudents}</b><span>Total Students</span><small>Assigned to courses</small></div>
            </div>
            <div className="cm-stat cm-stat--amber">
              <span className="cm-stat__icon"><CheckCircle2 size={26} /></span>
              <div><b>{totals.startedStudents || 0}</b><span>Started Courses</span><small>{pct(totals.startedStudents, totalStudents)}% of total students</small></div>
            </div>
            <div className="cm-stat cm-stat--pink">
              <span className="cm-stat__icon"><BarChart3 size={26} /></span>
              <div><b>{totals.completedStudents || 0}</b><span>Completed Courses</span><small>{pct(totals.completedStudents, totalStudents)}% of total students</small></div>
            </div>
          </section>

          {/* The row picked in the sidebar (the category, or one of its subcategories): where it is and
              the subcategories inside it, each with how much content it holds. Its content is listed below. */}
          {category && (
            <section className="cm-subcard" aria-label={`Inside ${where}`}>
              <div className="cm-subcard__head">
                <SubcategoryCrumbs
                  category={category}
                  icon={categoryIcon(categoryMeta(categories, category))}
                  chain={chain}
                  onPick={setSubcategory}
                />
                <SubcategoryActions
                  label={here}
                  onAddSubcategory={canAddHere ? () => addSidebarSubcategory(category, subcategoryNode) : null}
                />
                {isGCompris && (
                  <button type="button" className="cm-btn cm-btn--coins cm-btn--sm" aria-label="GCompris coin settings" title="Coins students earn in GCompris games" onClick={() => setGcomprisOpen(true)}>
                    <Coins size={15} /> Coin Settings
                  </button>
                )}
                {canDeleteHere && (
                  <button type="button" className="cm-btn cm-btn--danger cm-btn--sm" aria-label={`Delete ${hereKind} ${here}`} title={`Delete ${here}`} onClick={deleteHere}>
                    <Trash2 size={15} /> Delete {subcategoryNode ? 'Subcategory' : 'Category'}
                  </button>
                )}
              </div>
              <div className="cm-subcard__row">
                <span className="cm-subcard__label"><FolderTree size={16} aria-hidden="true" />Subcategories in {here}</span>
                {childNodes.length ? (
                  <div className="cm-subnodes" role="list">
                    {childNodes.map((node) => {
                      const placed = categoryContent.filter(({ course }) => courseInSubcategory(course, node)).length;
                      return (
                        <button key={node.key} type="button" role="listitem" className="cm-subnode" aria-label={`Open ${node.label}`} onClick={() => setSubcategory(node.key)}>
                          <span aria-hidden="true">{node.icon}</span>
                          {node.label}
                          {node.children.length > 0 && <small className="cm-subnode__count">{plural(node.children.length, 'subcategory', 'subcategories')}</small>}
                          <small className="cm-subnode__count">{plural(placed, 'content', 'contents')}</small>
                          <ChevronRight size={15} aria-hidden="true" />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <span className="cm-subcard__none">No subcategories inside {here}{canAddHere ? ' yet' : ''}.</span>
                )}
              </div>
            </section>
          )}

          <section className="cm-card">
            <div className="cm-card__head">
              <h2>{category ? `${where} Content` : 'All Content'} ({filtered.length})</h2>
              <div className="cm-filters">
                <select value={category} onChange={(e) => selectCategory(e.target.value)} aria-label="Category">
                  <option value="">All Categories</option>
                  {categories.map((c) => <option key={c.name} value={c.name}>{c.label || c.name}</option>)}
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
                <label className="cm-search">
                  <Search size={15} />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search content..." aria-label="Search in the list" />
                </label>
              </div>
            </div>

            <div className="cm-table-wrap">
              <table className="cm-table cm-table--content">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Content</th>
                    <th>Category</th>
                    <th>Items</th>
                    <th className="is-num">ISF Coins</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} className="cm-empty">Loading content…</td></tr>
                  ) : error ? (
                    <tr><td colSpan={7} className="cm-empty">{error} <button type="button" className="cm-link" onClick={load}>Try again</button></td></tr>
                  ) : !pageRows.length ? (
                    <tr><td colSpan={7} className="cm-empty">{contentRows.length ? 'No content matches these filters.' : 'No content yet. Pick a subcategory on the left to add some.'}</td></tr>
                  ) : pageRows.map((row, i) => {
                    const { course, chapter } = row;
                    const levels = levelsOf(course);
                    const title = chapter.title || 'Untitled';
                    const items = (chapter.contentItems || []).map((item) => ITEM_TYPES[item.type] || ITEM_TYPES.file);
                    return (
                      // Clicking the row opens the content's details, same as the 👁 button; the
                      // action buttons keep their own behaviour, and selecting text doesn't open it.
                      <tr
                        key={row.key}
                        className="cm-row-link"
                        onClick={(e) => {
                          if (e.target.closest('button, a, input, select, textarea, label')) return;
                          if (window.getSelection?.().toString()) return;
                          setDetails(row);
                        }}
                      >
                        <td>{(currentPage - 1) * PAGE_SIZE + i + 1}</td>
                        <td>
                          <div className="cm-course">
                            <span className="cm-course__thumb">
                              <span aria-hidden="true">{levels[levels.length - 1]?.icon || categoryMeta(categories, course.category).icon || '📚'}</span>
                            </span>
                            <div>
                              <b>{title}</b>
                              {chapter.description && <small>{chapter.description}</small>}
                              {/* Its subcategory under its category: Action Songs › Vocal Songs. */}
                              {levels.length > 0 && (
                                <small className="cm-subpath" title={`${categoryLabel(course.category)} › ${levels.map((node) => node.label).join(' › ')}`}>
                                  <FolderTree size={12} aria-hidden="true" />
                                  {levels.map((node, k) => (
                                    <React.Fragment key={node.key}>{k > 0 && ' '}<span>{k > 0 && '› '}{node.label}</span></React.Fragment>
                                  ))}
                                </small>
                              )}
                            </div>
                          </div>
                        </td>
                        <td><CategoryBadge categories={categories} name={course.category} /></td>
                        <td>
                          {items.length ? (
                            <span className="cm-content-items" title={items.map((type) => type.label).join(', ')}>
                              {items.map((type, k) => <span key={k} role="img" aria-label={type.label}>{type.icon}</span>)}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="is-num"><span className="cv-coin-chip"><i>₹</i>{contentCoins(chapter)}</span></td>
                        <td><span className={`cm-pill cm-pill--${course.status === 'published' ? 'green' : course.status === 'draft' ? 'amber' : 'gray'}`}>{course.status ? course.status[0].toUpperCase() + course.status.slice(1) : '—'}</span></td>
                        <td>
                          <div className="cm-row-actions">
                            <button type="button" className="cm-act cm-act--view" title="View content" aria-label={`View ${title}`} onClick={() => setDetails(row)}><Eye size={16} /></button>
                            <button type="button" className="cm-act cm-act--edit" title="Edit content" aria-label={`Edit ${title}`} onClick={() => editContent(row)}><Pencil size={16} /></button>
                            <button type="button" className="cm-act cm-act--delete" title="Delete content" aria-label={`Delete ${title}`} onClick={() => deleteContent(row)}><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <footer className="cm-pager">
                <span>Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                <div>
                  <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} aria-label="Previous page">‹</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <button key={n} type="button" className={n === currentPage ? 'is-active' : ''} onClick={() => setPage(n)}>{n}</button>
                  ))}
                  <button type="button" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} aria-label="Next page">›</button>
                </div>
              </footer>
            )}
          </section>
        </main>
      </div>

      {details && (
        <ContentDetailsDialog
          row={details}
          where={[categoryLabel(details.course.category), ...levelsOf(details.course).map((node) => node.label)].join(' › ')}
          onClose={() => setDetails(null)}
          onChanged={load}
        />
      )}

      {assignFor !== undefined && (
        <AssignCourseModal
          categories={categories}
          courses={courses}
          initialCourse={assignFor}
          onClose={() => setAssignFor(undefined)}
          onAssigned={() => { setAssignFor(undefined); load(); }}
        />
      )}

      <GComprisRewardModal isOpen={gcomprisOpen} onClose={() => setGcomprisOpen(false)} />
    </div>
  );
}
